import { useSignal } from '@preact/signals';

import { interpolate } from '@amumin/i18n';
import { Button, Dialog, Notice, TextField } from '@amumin/ui';
import { Icon } from '@amumin/ui-core';

import { api, errorMessage, isApiError } from '../api.js';
import { StepUpCancelled, stepUpRequest } from '../lib/stepUp.js';
import { appHost } from '../lib/addresses.js';
import { appText, context, messages } from '../state/session.js';

/** Wachtwoordvenster bij `403 step_up_required` (ONTWERP.md §11.1.1 step-up). */
export function StepUpDialog() {
  const request = stepUpRequest.value;
  const m = messages.value;
  const password = useSignal('');
  const error = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);

  if (!request) return null;

  const finish = () => {
    stepUpRequest.value = null;
    password.value = '';
    error.value = undefined;
    busy.value = false;
  };

  const submit = async () => {
    if (busy.value) return;
    busy.value = true;
    error.value = undefined;
    try {
      const res = await api.app.auth.stepUp(password.value);
      if (context.value) {
        context.value = { ...context.value, step_up_valid_until: res.step_up_valid_until };
      }
      const r = request;
      finish();
      r.resolve();
    } catch (e) {
      busy.value = false;
      error.value = isApiError(e, 401, 'invalid_credentials')
        ? m.web.stepUpWrong
        : errorMessage(e, m);
    }
  };

  const cancel = () => {
    const r = request;
    finish();
    r.reject(new StepUpCancelled());
  };

  // Golf 6a (oprichter 26 sep #2: "het moet duidelijker zijn welk wachtwoord ik daar moet
  // invullen"): welk wachtwoord (het eigen AMUMIN-wachtwoord, niet de wifi of de iPad-code),
  // waarom we het vragen, voor welk account, en "Wachtwoord vergeten?". Elk account heeft een
  // wachtwoord (inloggen met alleen een code bestaat niet): de e-mailcode is de tweede stap bij
  // het inloggen buiten een vertrouwd netwerk, nooit een vervanging van het wachtwoord.
  const g = appText.value.golf6.stepUp;
  const email = context.value?.identity.email ?? '';
  const host = appHost();
  // Een echt <dialog> (toplaag van de browser): zo staat het wachtwoordvenster ook bóven een ander
  // venster (bv. "Koppel met QR-code") en is het daar bruikbaar; een gewone laag eronder was
  // onbereikbaar (de rest van de pagina is inert zolang een modaal <dialog> open is).
  return (
    <Dialog
      open
      onClose={cancel}
      title={m.web.stepUpTitle}
      closeLabel={m.design.dialog.close}
      dismissible={false}
      name="step-up-dialog"
    >
      <div>
        <div class="stepup-explain">
          <p class="stepup-which" id="stepup-which" data-name="stepup-which">
            <Icon id="key-round" size={20} />
            <span>
              {interpolate(g.which, { host })} {g.notThese}
            </span>
          </p>
          <p class="small muted" id="stepup-title">
            {g.why}
          </p>
          {email ? (
            <p class="stepup-account small" data-name="stepup-account">
              <Icon id="circle-user-round" size={16} />
              <span>
                {g.account}: <strong dir="ltr">{email}</strong>
              </span>
            </p>
          ) : null}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {error.value ? <Notice kind="error">{error.value}</Notice> : null}
          {/* Verborgen gebruikersnaam: wachtwoordbeheerders vullen zo het juiste account in. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            hidden
          />
          <TextField
            id="stepup-password"
            label={g.password}
            type="password"
            value={password.value}
            onInput={(v) => (password.value = v)}
            autoComplete="current-password"
            autoFocus
            required
          />
          <p class="small">
            <a href="/reset" target="_blank" rel="noopener" data-name="stepup-forgot">
              {g.forgot}
            </a>
          </p>
          <div class="row row-end">
            <Button name="stepup-cancel" onPress={cancel}>
              {m.common.cancel}
            </Button>
            <Button name="stepup-confirm" type="submit" variant="primary" busy={busy.value}>
              {m.web.stepUpButton}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
